/*----------------------------------
- AGENDA ROUTE
----------------------------------*/

/**
 * @docs docs/features/agenda
 * @adr  ADR-0055
 * @rule SSR on every request, never static: the query carries the state, and a static page is
 *       cached by path whatever the query. A month past the window's start answers a 301 to the
 *       same URL without it (an old Wednesday email still lands), a month beyond its end
 *       NotFound, both before any Product call; any other agenda URL reaches its normalized form
 *       in one 301: decided before any Product call, or from Product's echo when the query names
 *       a TLD. The canonical is always `/agenda` and only the default state is indexable.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { NotFound } from '@common/errors';
import type ApiClient from '@common/router/request/api';
import type { TFetcher } from '@common/router/request/api';
import {
    AGENDA_ICS_PATH,
    AGENDA_PATH,
    agendaCategoryIcsPath,
    isDefaultAgendaState,
    type PublicAgendaResponse,
} from '@uniquedomains/components/contracts/agenda/public';
import { AGENDA_PAGE_MIRROR_PATH } from '@uniquedomains/core/domains/tlds';

import { AGENDA_PAGE_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import {
    PUBLIC_REVALIDATE_HEADERS,
    PublicPageUnavailable,
    setPublicUnavailableHeaders,
} from '@/client/pages/landing/lib/publicUnavailable';
import { buildAgendaStructuredData, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { resolveAgendaRobotsDirectives } from '@/common/catalogs/crawlerPolicy';
import { AGENDA_GATEWAY_PATH } from '@/common/contracts/agendaGateway';
import type { PublicReadResult } from '@/common/contracts/publicReadResult';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import {
    resolveAgendaExpiredMonthRedirect,
    resolveAgendaPageState,
    resolveAgendaRedirect,
    resolveAgendaRedirectBeforeRead,
    toAgendaPageReadRequest,
} from '@/common/libs/agenda/pageState';
import AgendaPage from '../page/AgendaPage';
import { formatAgendaCountry } from './presentation';

/*----------------------------------
- TYPES
----------------------------------*/

type AgendaProductController = {
    Navigation: {
        GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation>;
    };
};

type AgendaRouteRequest = {
    url: string;
    res?: {
        setHeader: (name: string, value: string) => void;
    };
};

type AgendaRouteDataContext = {
    Product: AgendaProductController;
    api: Pick<ApiClient, 'createFetcher'>;
    request: AgendaRouteRequest;
};

type AgendaRouteResponse = {
    canonicalUrl?: URL;
    redirect?: (url: string, code?: number) => void;
    setHeaders?: (headers: Record<string, string>) => void;
};

type AgendaPageDocument = {
    description?: string;
    head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
    jsonld: WebsiteJsonLdNode[];
    metas: Record<string, string | Date | null | undefined>;
    title?: string;
    url: string;
};

type AgendaRouteRenderContext = {
    /** Set when the query is not the normalized one: render answers the 301 and nothing else. */
    redirectTo: string | null;
    /** The request's clock, carried to render and hydration so both read the same month. */
    nowIso: string;
    agendaResult: PublicReadResult<PublicAgendaResponse> | null;
    navigation: LandingHeaderDomainsNavigation | null;
    page: AgendaPageDocument;
    request: AgendaRouteRequest;
    response?: AgendaRouteResponse;
    Router: { url: (path: string) => string };
};

/*----------------------------------
- HELPERS
----------------------------------*/

const toRequestUrl = (request: AgendaRouteRequest): URL => new URL(request.url, 'https://unique.domains');

const addAgendaHeadLinks = ({
    page,
    robotsDirectives,
    Router,
    icsPath,
}: {
    page: AgendaPageDocument;
    robotsDirectives: string;
    Router: { url: (path: string) => string };
    icsPath: string;
}) => {
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/calendar',
        title: AGENDA_PAGE_CONTENT.meta.calendarFeedTitle,
        href: Router.url(icsPath),
    });
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        title: AGENDA_PAGE_CONTENT.meta.markdownTitle,
        href: Router.url(AGENDA_PAGE_MIRROR_PATH),
    });
    page.head.push({ $: 'meta', name: 'robots', content: robotsDirectives });
};

/*----------------------------------
- ROUTE
----------------------------------*/

export const buildAgendaRouteData = ({ Product, api, request }: AgendaRouteDataContext) => {
    const now = new Date();
    const requestUrl = toRequestUrl(request);
    const state = resolveAgendaPageState(requestUrl.searchParams, now);
    // A well-formed month outside the window is not a page, and costs no Product call: one that
    // has gone past opens the current month (an old email's link), one ahead was never linked.
    const redirectTo = state.outOfWindow
        ? resolveAgendaExpiredMonthRedirect(requestUrl, state, now)
        : resolveAgendaRedirectBeforeRead(requestUrl, state, now);
    if (state.outOfWindow && !redirectTo) throw new NotFound();
    if (redirectTo) return { redirectTo, nowIso: now.toISOString(), agendaResult: null, navigation: null };

    return {
        redirectTo: null,
        nowIso: now.toISOString(),
        agendaResult: api.createFetcher<PublicReadResult<PublicAgendaResponse>>(
            'POST',
            AGENDA_GATEWAY_PATH,
            toAgendaPageReadRequest(state.filters),
        ),
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    };
};

export const renderAgendaRoute = ({
    redirectTo,
    nowIso,
    agendaResult,
    navigation,
    page,
    request,
    response,
    Router,
}: AgendaRouteRenderContext) => {
    if (redirectTo) {
        response?.redirect?.(Router.url(redirectTo), 301);
        return null;
    }
    if (!agendaResult || agendaResult.status === 'unavailable') {
        setPublicUnavailableHeaders(request, response);
        throw new PublicPageUnavailable(AGENDA_PAGE_CONTENT.unavailable);
    }

    const now = new Date(nowIso);
    const requestUrl = toRequestUrl(request);
    const agendaResponse = agendaResult.value;
    if (agendaResponse.outOfWindow) throw new NotFound();

    // Product echoes the state it applied; the TLD is its allowlist (only a known TLD comes
    // back). A different echo means the URL names a state that does not exist: 301 to the one
    // Product applied, which also normalizes the rest of a query naming a TLD (see data). The
    // month stays the page's own (it was sent explicitly), so a clock difference between the two
    // apps at midnight on the 1st never bounces a visitor.
    const requested = resolveAgendaPageState(requestUrl.searchParams, now);
    const filters = { ...agendaResponse.filters, month: requested.filters.month };
    const echoRedirect = resolveAgendaRedirect(requestUrl, filters, now);
    if (echoRedirect) {
        response?.redirect?.(Router.url(echoRedirect), 301);
        return null;
    }
    const agenda: PublicAgendaResponse = { ...agendaResponse, filters };

    const canonicalUrl = Router.url(AGENDA_PATH);
    const { pageTitle, metaDescription } = AGENDA_PAGE_CONTENT.meta;

    page.title = pageTitle;
    page.description = metaDescription;
    page.url = canonicalUrl;
    page.metas = {
        ...page.metas,
        'og:description': metaDescription,
        'og:title': pageTitle,
        'og:type': 'website',
        'og:url': canonicalUrl,
        'twitter:card': 'summary_large_image',
        'twitter:description': metaDescription,
        'twitter:title': pageTitle,
        'twitter:url': canonicalUrl,
    };
    addAgendaHeadLinks({
        page,
        robotsDirectives: resolveAgendaRobotsDirectives(isDefaultAgendaState(agenda.filters, now)),
        Router,
        icsPath: agenda.filters.category ? agendaCategoryIcsPath(agenda.filters.category) : AGENDA_ICS_PATH,
    });
    page.jsonld.push(
        ...buildAgendaStructuredData({
            canonicalUrl,
            description: metaDescription,
            entries: agenda.entries,
            formatCountry: formatAgendaCountry,
            title: pageTitle,
        }),
    );

    if (response) response.canonicalUrl = new URL(canonicalUrl);
    // Revalidated on every hit, so a filter change or a new registry date shows at once: the
    // per-state read cache lives in the Website process (`server/lib/agendaPublicRead.ts`).
    response?.setHeaders?.(PUBLIC_REVALIDATE_HEADERS);

    return <AgendaPage agenda={agenda} navigation={navigation} nowIso={nowIso} />;
};
